import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userManagementApi } from '../api/userManagementApi';
import { userManagementQueryKeys } from './queryKeys';
import {
  GetUsersWithRolesRequestDTO,
  UsersWithRolesResponseDTO,
  InviteUserWithRoleDTO,
  InvitationWithRoleResponseDTO,
} from '../dtos';
import {
  UserQueryParams,
  UseUsersWithRolesOptions,
  UsersWithRolesResult,
  PaginationInfo,
  DEFAULT_PAGE_SIZE,
  DEFAULT_STALE_TIME,
} from '../types';
import { notificationService } from '@shared/components/notifications';

/**
 * Hook for fetching users with roles with comprehensive filtering, sorting, and pagination
 * @param params Query parameters for filtering, sorting, and pagination
 * @param options React Query options
 * @returns UsersWithRolesResult with data, loading state, and controls
 */
export const useUsersWithRoles = (
  params: UserQueryParams = {},
  options: UseUsersWithRolesOptions = {}
): UsersWithRolesResult => {
  // Convert client params to API request DTO
  const requestParams: GetUsersWithRolesRequestDTO = {
    role: params.role,
    tenantId: params.tenantId,
    domainId: params.domainId,
    subjectId: params.subjectId,
    status: params.status,
    email: params.email,
    name: params.name,
    includeExpiredRoles: params.includeExpiredRoles,
    includeUsersWithoutRoles: params.includeUsersWithoutRoles,
    limit: params.limit || DEFAULT_PAGE_SIZE,
    offset: params.page ? (params.page - 1) * (params.limit || DEFAULT_PAGE_SIZE) : 0,
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  };

  const query = useQuery({
    queryKey: userManagementQueryKeys.usersWithRoles(requestParams),
    queryFn: async (): Promise<UsersWithRolesResponseDTO> => {
      const response = await userManagementApi.getUsersWithRoles(requestParams);
      return response.data || { users: [], totalCount: 0, hasMore: false };
    },
    enabled: options.enabled !== false,
    staleTime: options.staleTime || DEFAULT_STALE_TIME,
    refetchOnWindowFocus: options.refetchOnWindowFocus || false,
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return typeof options.retry === 'number'
        ? failureCount < options.retry
        : typeof options.retry === 'boolean'
          ? options.retry
          : failureCount < 3;
    },
    placeholderData: options.keepPreviousData ? (previousData: any) => previousData : undefined,
  });

  // Calculate pagination info
  const paginationInfo: PaginationInfo = {
    page: params.page || 1,
    limit: params.limit || DEFAULT_PAGE_SIZE,
    totalCount: query.data?.totalCount || 0,
    totalPages: Math.ceil((query.data?.totalCount || 0) / (params.limit || DEFAULT_PAGE_SIZE)),
    hasMore: query.data?.hasMore || false,
    nextToken: query.data?.nextToken,
  };

  return {
    data: query.data?.users || [],
    totalCount: query.data?.totalCount || 0,
    pagination: paginationInfo,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
};

/**
 * Mutation hook for inviting users with roles
 * @param onSuccess Callback for successful invitation
 * @param onError Callback for invitation error
 * @returns Mutation object for inviting users
 */
export const useInviteUserWithRole = (
  onSuccess?: (data: InvitationWithRoleResponseDTO) => void,
  onError?: (error: any) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InviteUserWithRoleDTO) => {
      const response = await userManagementApi.inviteUserWithRole(data);
      return response.data;
    },
    onSuccess: (result, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.all });

      // Invalidate specific tenant and role queries
      if (variables.tenantId) {
        queryClient.invalidateQueries({
          queryKey: userManagementQueryKeys.byTenant(variables.tenantId),
        });
      }
      if (variables.role) {
        queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.byRole(variables.role) });
      }
      if (variables.domainId) {
        queryClient.invalidateQueries({
          queryKey: userManagementQueryKeys.byDomain(variables.domainId),
        });
      }
      if (variables.subjectId) {
        queryClient.invalidateQueries({
          queryKey: userManagementQueryKeys.bySubject(variables.subjectId),
        });
      }

      // Show success notification
      if (result?.success) {
        notificationService.success(result.message || 'User invited successfully');
      }

      // Call custom success callback
      if (onSuccess && result) {
        onSuccess(result);
      }
    },
    onError: (error: any, _variables) => {
      const errorMessage = error?.message || 'Failed to invite user';
      notificationService.error(errorMessage);

      // Call custom error callback
      if (onError) {
        onError(error);
      }
    },
  });
};

/**
 * Hook for checking if a user exists by email
 * @param email Email to check
 * @param enabled Whether the query should be enabled
 * @returns Query result with user existence information
 */
export const useUserExists = (email: string, enabled = true) => {
  return useQuery({
    queryKey: userManagementQueryKeys.userExists(email),
    queryFn: async () => {
      const response = await userManagementApi.checkUserExists(email);
      return response.data;
    },
    enabled: enabled && !!email,
    staleTime: 30 * 1000, // 30 seconds - user existence changes infrequently
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

/**
 * Hook for prefetching users with roles for better UX
 * @param params Query parameters for prefetching
 */
export const usePrefetchUsersWithRoles = () => {
  const queryClient = useQueryClient();

  const prefetchUsersWithRoles = async (params: UserQueryParams = {}) => {
    const requestParams: GetUsersWithRolesRequestDTO = {
      role: params.role,
      tenantId: params.tenantId,
      domainId: params.domainId,
      subjectId: params.subjectId,
      status: params.status,
      email: params.email,
      name: params.name,
      includeExpiredRoles: params.includeExpiredRoles,
      includeUsersWithoutRoles: params.includeUsersWithoutRoles,
      limit: params.limit || DEFAULT_PAGE_SIZE,
      offset: params.page ? (params.page - 1) * (params.limit || DEFAULT_PAGE_SIZE) : 0,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
    };

    await queryClient.prefetchQuery({
      queryKey: userManagementQueryKeys.usersWithRoles(requestParams),
      queryFn: async () => {
        const response = await userManagementApi.getUsersWithRoles(requestParams);
        return response.data || { users: [], totalCount: 0, hasMore: false };
      },
      staleTime: DEFAULT_STALE_TIME,
    });
  };

  return { prefetchUsersWithRoles };
};

/**
 * Hook for invalidating user management queries
 * @returns Functions to invalidate specific query types
 */
export const useInvalidateUserQueries = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.all });
  };

  const invalidateByTenant = (tenantId: string) => {
    queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.byTenant(tenantId) });
  };

  const invalidateByRole = (role: string) => {
    queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.byRole(role) });
  };

  const invalidateByDomain = (domainId: string) => {
    queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.byDomain(domainId) });
  };

  const invalidateBySubject = (subjectId: string) => {
    queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.bySubject(subjectId) });
  };

  const invalidateUsersWithRoles = (params?: GetUsersWithRolesRequestDTO) => {
    queryClient.invalidateQueries({ queryKey: userManagementQueryKeys.usersWithRoles(params) });
  };

  return {
    invalidateAll,
    invalidateByTenant,
    invalidateByRole,
    invalidateByDomain,
    invalidateBySubject,
    invalidateUsersWithRoles,
  };
};
